import type { Metadata } from "next";

// The site's languages, as in Next's internationalization guide: the locale is
// in the path, and the default locale has no prefix (/pricing, /fr/pricing). The
// English routes stay in app/(marketing); app/[lang] serves every other locale,
// from a dictionary per locale (dictionaries/). To add one: list it here, add its
// paths below, and add its dictionary to app/[lang]/dictionaries.ts.
export const LOCALES = ["en", "fr"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE = "en" satisfies Locale;

export type TranslatedLocale = Exclude<Locale, typeof DEFAULT_LOCALE>;
export const TRANSLATED_LOCALES = ["fr"] as const satisfies readonly TranslatedLocale[];

// In the language itself, for the language links in the footer.
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  fr: "Français",
};

const OPEN_GRAPH_LOCALES: Record<Locale, string> = {
  en: "en_US",
  fr: "fr_FR",
};

// The pages each locale has, by their path without the prefix. Everything else
// on a translated page links to the English page.
export const TRANSLATED_PATHS: Record<TranslatedLocale, readonly string[]> = {
  fr: [
    "/",
    "/pricing",
    "/tools/youtube-to-shorts",
    "/tools/ai-video-editor",
    "/alternatives/opus-clip",
    "/alternatives/remakeit",
    "/alternatives/submagic",
    "/integrations/make",
  ],
};

// Translated pages with no English version, so no `en` or `x-default` hreflang.
const NO_ENGLISH_VERSION: ReadonlySet<string> = new Set([
  "/alternatives/remakeit",
]);

export const hasLocalePage = (lang: Locale, path: string): boolean =>
  lang === DEFAULT_LOCALE
    ? !NO_ENGLISH_VERSION.has(path)
    : TRANSLATED_PATHS[lang].includes(path);

// /pricing in French is /fr/pricing.
export const localePath = (lang: Locale, path: string): string => {
  if (lang === DEFAULT_LOCALE) return path;
  return path === "/" ? `/${lang}` : `/${lang}${path}`;
};

// A link on a `lang` page: the page in that language if it has one.
export const localizedHref = (lang: Locale, path: string): string =>
  hasLocalePage(lang, path) ? localePath(lang, path) : path;

// /fr/pricing is /pricing in French, /pricing is /pricing in English.
export const parseLocalePath = (
  pathname: string,
): { lang: Locale; path: string } => {
  const [, first, ...rest] = pathname.split("/");
  const lang = TRANSLATED_LOCALES.find((locale) => locale === first);
  if (!lang) return { lang: DEFAULT_LOCALE, path: pathname };
  return { lang, path: `/${rest.join("/")}` };
};

// The same page in another language, for the language links: that language's
// home page when the page has no version there.
export const languageSwitchHref = (pathname: string, to: Locale): string => {
  const { path } = parseLocalePath(pathname);
  return localePath(to, hasLocalePage(to, path) ? path : "/");
};

type Languages = Record<string, string>;

// A page's hreflang set: every language that has it, with English as the default
// for everyone else. Undefined for a page that only exists in English.
export const languagesFor = (path: string): Languages | undefined => {
  const translated = TRANSLATED_LOCALES.filter((lang) =>
    hasLocalePage(lang, path),
  );
  if (translated.length === 0) return undefined;
  const languages: Languages = {};
  if (hasLocalePage(DEFAULT_LOCALE, path)) {
    languages[DEFAULT_LOCALE] = path;
    languages["x-default"] = path;
  }
  for (const lang of translated) languages[lang] = localePath(lang, path);
  return languages;
};

// `alternates` for a page's metadata. Google only honours an hreflang pair when
// both pages list each other, so English and translated pages share this.
export const alternatesFor = (
  path: string,
  lang: Locale = DEFAULT_LOCALE,
): NonNullable<Metadata["alternates"]> => {
  const languages = languagesFor(path);
  return {
    canonical: localePath(lang, path),
    ...(languages && { languages }),
  };
};

// `locale` and `alternateLocale` for a page's Open Graph tags. Nothing for a page
// that only exists in English.
export const openGraphLocales = (
  path: string,
  lang: Locale = DEFAULT_LOCALE,
): { locale?: string; alternateLocale?: string[] } => {
  if (!languagesFor(path)) return {};
  const alternates = LOCALES.filter(
    (other) => other !== lang && hasLocalePage(other, path),
  );
  return {
    locale: OPEN_GRAPH_LOCALES[lang],
    ...(alternates.length > 0 && {
      alternateLocale: alternates.map((other) => OPEN_GRAPH_LOCALES[other]),
    }),
  };
};
